import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, CheckCircle, Shield, Zap, Gift } from 'lucide-react';
import { supabase } from '../services/supabase';
import { auth } from '../services/auth';
import { logger } from '../services/logger';
import { storeReferralCode, getStoredReferralCode, clearStoredReferralCode, processReferral } from '../services/referral';
import { redditPixel } from '../services/reddit-pixel';
import { tracking } from '../utils/tracking';

export function SignupPage() {
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hasReferral, setHasReferral] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    tracking.signupPageViewed();

    const refCode = searchParams.get('ref');
    if (refCode) {
      storeReferralCode(refCode);
      setHasReferral(true);
    } else if (getStoredReferralCode()) {
      setHasReferral(true);
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    tracking.signupFormSubmitted();

    try {
      if (!email.trim() || !password.trim()) {
        throw new Error('Please enter both email and password');
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password: password.trim(),
        options: {
          data: {
            email: email.trim()
          }
        }
      });

      if (signUpError) throw signUpError;

      if (signUpData.user) {
        tracking.signupCompleted();
        redditPixel.trackSignUp();
        redditPixel.trackLead();

        const storedCode = getStoredReferralCode();
        if (storedCode) {
          await processReferral(signUpData.user.id, storedCode);
          clearStoredReferralCode();
        }
        navigate('/app');
      }
    } catch (err: any) {
      logger.error('Signup error:', err);
      tracking.signupError(err.message || 'unknown');
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignup() {
    setGoogleLoading(true);
    setError('');
    tracking.signupFormSubmitted();

    try {
      await auth.signInWithGoogle();
    } catch (err: any) {
      logger.error('Google signup error:', err);
      tracking.signupError(err.message || 'unknown');
      setError(err.message || 'Failed to sign up with Google. Please try again.');
      setGoogleLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ink-950 via-ink-900 to-ink-800 pt-28 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>

      <div className="relative max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white space-y-8 hidden lg:block">
            <div>
              <h1 className="text-5xl font-heading font-bold mb-4 leading-tight">
                Your coach is ready.
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Tell it what's on your mind. Get a real response — not generic advice — in under a minute.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { label: "Tell your coach what's on your mind", sublabel: "Career, relationships, habits, overthinking — anything." },
                { label: "Get a personalized response", sublabel: "Your coach asks the right questions to cut through the noise." },
                { label: "Leave with a concrete next step", sublabel: "Not motivation. An actual action you can take today." }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-gold-500 text-ink-950 font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{step.label}</p>
                    <p className="text-sm text-gray-400">{step.sublabel}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-ink-900/50 border border-gray-800 rounded-2xl p-6 backdrop-blur-sm">
              <div className="text-gold-500 font-bold text-sm uppercase tracking-wide mb-4">Free plan includes:</div>
              <div className="space-y-3">
                {[
                  { icon: CheckCircle, text: '3 coaching sessions per week' },
                  { icon: Zap, text: '10 journal entries per month' },
                  { icon: Shield, text: 'Private by default — we never train on your data' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <item.icon className="h-5 w-5 text-success-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  Not therapy. No commitment. Upgrade anytime for unlimited access — $9/month.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6 text-sm text-gray-400">
              <div className="flex items-center">
                <Shield className="h-4 w-4 mr-2 text-gold-500" />
                <span>Free forever option</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-gold-500" />
                <span>No credit card required</span>
              </div>
            </div>
          </div>

          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              {hasReferral && (
                <div className="mb-6 p-4 bg-gradient-to-r from-gold-50 to-amber-50 border border-gold-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold-500 flex items-center justify-center flex-shrink-0">
                      <Gift className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-ink-950">You've been invited!</p>
                      <p className="text-sm text-gray-600">Sign up now and get an extended 37-day trial</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-8">
                <h2 className="text-3xl font-heading font-bold text-ink-950 mb-2">
                  Create your free account
                </h2>
                <p className="text-gray-600">
                  No credit card. No commitment. Cancel anytime.
                </p>
              </div>

              {error && (
                <div className="bg-error-500/10 border border-error-500/20 text-error-500 p-4 rounded-lg text-sm flex items-center mb-6">
                  <span className="flex-1">{error}</span>
                  <button
                    onClick={() => setError('')}
                    className="text-error-500 hover:text-error-400 ml-2 text-xl leading-none"
                    aria-label="Dismiss error"
                  >
                    ×
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all"
                      placeholder="Create a password (min. 6 characters)"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || googleLoading}
                  className="w-full flex justify-center py-3.5 px-4 border border-transparent text-base font-bold rounded-lg text-ink-950 bg-gold-500 hover:bg-gold-500/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-ink-950 border-t-transparent mr-2" />
                      Creating account...
                    </div>
                  ) : (
                    'Get started free'
                  )}
                </button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignup}
                disabled={loading || googleLoading}
                className="w-full flex items-center justify-center py-3.5 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {googleLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-ink-950 border-t-transparent mr-2" />
                    <span className="text-gray-700 font-medium">Connecting...</span>
                  </div>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-gray-700 font-medium">Continue with Google</span>
                  </>
                )}
              </button>

              <div className="mt-6 text-center space-y-3">
                <p className="text-xs text-gray-500 leading-relaxed">
                  By signing up, you agree to our{' '}
                  <Link to="/terms" className="text-gold-500 hover:text-gold-500/80 font-medium">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-gold-500 hover:text-gold-500/80 font-medium">
                    Privacy Policy
                  </Link>
                </p>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold text-ink-950 hover:text-gray-700 transition-colors">
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:hidden mt-6 text-center text-sm text-gray-400 space-y-3">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center">
                  <Shield className="h-4 w-4 mr-1.5 text-gold-500" />
                  <span>Free forever</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1.5 text-gold-500" />
                  <span>No credit card</span>
                </div>
              </div>
              <div className="space-y-1.5 text-gray-500">
                <p>3 coaching sessions per week</p>
                <p>10 journal entries per month</p>
                <p>Upgrade anytime for unlimited access</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}